import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../services/api";

import "react-toastify/dist/ReactToastify.css";
import TablePagination from "../../../components/TablePagination";
import moment from "../../../utils/momentConfig";
import { TypeHistory } from "../../../models/enums/TypeHistory";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faFileSignature, faPrint } from "@fortawesome/free-solid-svg-icons";
import DialogConfirm from "../../../components/DialogConfirm";
import { toast } from "react-toastify";
import toastOptions from "../../../utils/toastOptions";
import { utils } from "../../../utils/utils";
import ILetterDetailDTO from "../../../models/ILetterDetailDTO";
import { StatusType } from "../../../models/enums/StatusType";

interface ListDTO {
  id: number;
  createdDate: Date;
  createdBy: string;
  member: {
    id: number;
    nameAndFunction: string;
    name: string;
    congregation: any;
  };
  note: string;
  reason: string;
  idHistoryClosedOn?: number;
  modifiedDate?: Date;
  status: StatusType;
  historyClosedOn: {
    modifiedBy: string;
  }
}

export default function LetterList() {
  const [refresh, setRefresh] = useState<boolean>();
  const [totalLetters, setTotalLetters] = useState(0);
  const [termSearch, setTermSearch] = useState<string>("");
  const [type, setType] = useState<string>();
  const [delayedSearchTerm, setDelayedSearchTerm] = useState('');
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [currentLetterId, setCurrentLetterId] = useState<number>(0);
  const [name, setName] = useState<string>('');
  const [ixLetter, setIxLetter] = useState<number>(0);

  const columns = [
    {
      name: "Nome",
      sortable: true,
      grow: 2,
      cell: (row: ListDTO) => <Link to={`/membros/${row.member.id}`} 
          uk-tooltip="title: Ver cadastro do membro"
          className="cursor-pointer"
          target="_blank"
        >
          <span>{row.member.nameAndFunction}</span><br />
          <span className="uk-text-meta uk-text-italic">({row.member.congregation.description})</span>
        </Link>
    },
    {
      name: "Tipo",
      sortable: true,
      cell: (row: ListDTO) => <span>{row.reason}</span>
    },
    {
      name: "Emitida",
      sortable: false,
      cell: (row: ListDTO) => <span>
        Em {moment(row.createdDate).format('DD/MM/yyyy HH:mm:ss')} <br/>
        Por {row.createdBy}
      </span>
    },
    {
      name: "Obs.",
      sortable: false,
      cell: (row: ListDTO) => <div className="uk-flex uk-flex-column">
        {row.reason === TypeHistory.CHANGE_LETTER && !row.idHistoryClosedOn &&
          <span className="uk-text-danger">
            Carta de Mudança pendente de assinatura!
          </span>
        }
        {handleNote(row)}
      </div>
    },
    {
      name: 'Ações',
      button: true,
      sortable: false,
      cell: (row: ListDTO) => (
        <div className='uk-flex uk-flex-between'>
          {
            ixLetter === row.id ? (
              <span uk-spinner="true;ratio: 0.5"></span>
            ) : <div>
              {row.reason === TypeHistory.CHANGE_LETTER &&              
                <a href="#" uk-tooltip={`title: ${row.idHistoryClosedOn ? 'Carta assinada' : 'Processar assinatura'}`}
                  className='uk-margin-small-right' 
                  onClick={(ev) => { 
                    ev.preventDefault();
                    
                    if (row.idHistoryClosedOn) return;

                    setCurrentLetterId(row.id);
                    setName(row.member.nameAndFunction); 
                    setOpenConfirmation(true); 
                  }}
                >
                  {row.idHistoryClosedOn ? <FontAwesomeIcon icon={faCheck} color="green" size="lg" /> : <FontAwesomeIcon icon={faFileSignature} />}
                </a>
              }
              <a href="#" uk-tooltip={`title: Reimprimir Carta de ${row.reason === TypeHistory.CHANGE_LETTER ? 'Mudança' : 'Recomendação'}`}
                className='uk-margin-small-right'
                onClick={(ev) => { ev.preventDefault(); handlePrintLetter(row) }}
              >
                <FontAwesomeIcon icon={faPrint} />
              </a>
            </div>
          }
        </div>
      )
    }
  ];

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      setDelayedSearchTerm(termSearch);
    }, 500); // You can adjust the delay time according to your preferences

    return () => clearTimeout(delaySearch);
  }, [termSearch]);

  useEffect(() => {
    // Perform the search operation with delayedSearchTerm
    // This will be called only when the user stops typing for the specified delay time
    setRefresh(!refresh);
  }, [delayedSearchTerm]);

  const fetchLetters = async (page: number, limit: number) => {
    try {
      setTotalLetters(0);

      let params = {
        page: page,
        limit: limit,
        type: type
      } as any;

      if (termSearch) {
        params = { ...params, name: termSearch?.toUpperCase() };
      }

      const response = await api.get(`letters/issued`, { params: params });

      setTotalLetters(response.data.total);

      return response;
    } catch {
      return { data: [], total: 0 };
    }
  };

  const handleSearch = async (search: string) => {  
    if (search.length > 2 || search.length === 0) {
      setTermSearch(search);
    }
  };

  const handleType = async(selectedType: string) => {
    setType(selectedType);
    setRefresh(!refresh);
  }

  const handleProcessLetter = async (confirmationCode: string) => {
    try {
      if (!confirmationCode) {
        toast.warning("Informe a senha", toastOptions);
        return;
      }

      const letter = {
        id: currentLetterId,
        confirmationCode: confirmationCode
      };
      
      setIxLetter(currentLetterId);
      dialogClose();
      await api.post(`letters/${currentLetterId}/response`, letter);
      toast.success(`Processo concluído com sucesso!`, toastOptions);
      setRefresh(!refresh);
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    } finally {
      setIxLetter(0);
    }
  }

  const dialogClose = () => {
    setOpenConfirmation(false);
  }

  const handlePrintLetter = async (row: ListDTO) => {
    try {
      setIxLetter(row.id);
      const request: any = {};
      request.idMember = row.member?.id;
      request.type = row.reason;
      request.note = row.note;
      request.createdDate = row.createdDate;
      request.id = row.id;

      const isRecommendationLetter = request.type === TypeHistory.RECOMMENDATION_LETTER;

      if (!isRecommendationLetter) {
        request.signPerson = utils.standardLetterSignPerson();
      }

      const { data } = await api.post(`letters/member/${row.member?.id}`, request);
    
      const nameLetter = `${row.member?.name.toLocaleLowerCase().split(' ')[0]}-carta-${isRecommendationLetter ? 'recomendação' : 'mudança'}-${new Date().getTime()}.pdf`;

      utils.printPDF(nameLetter, data.item.report);
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    } finally {
      setIxLetter(0);
    }
  };

  const handleNote = (row: ListDTO) => {
    if (row.idHistoryClosedOn) {
      return <div className="uk-text-italic">
        Carta de Mudança assinada em {moment(row.modifiedDate).format('DD/MM/yyyy HH:mm:ss')} por {row.historyClosedOn.modifiedBy}
      </div>
    }

    if (row.status === StatusType.INACTIVE) {
      return <div className="uk-text-danger">Carta Cancelada</div>
    }

    let note = row.note;

    if (!note) return;
  
    if (note.startsWith('{') && note.endsWith('}')) {
      const noteObj = JSON.parse(row.note) as ILetterDetailDTO;
      note = noteObj.formatedNote ?? '';
    }

    if (note?.includes('-')) {
      const lines = note.split('-');
      return <div className="uk-margin-small-top">{lines.map(l => <><span>{l}</span><br/><br/></>)}</div>
    }

    return note;
  }
  
  return (
    <div id="page-letter-list">
      <div className="uk-card uk-card-default uk-card-small uk-card-hover uk-margin-bottom">
        <div className="uk-card-header">
          <div className="uk-grid-small uk-flex-middle" uk-grid="true">
            <div className="uk-width-expand">
              <h3 className="uk-card-title uk-margin-remove-bottom">
                Cartas Emitidas
              </h3>
            </div>
            <Link
              className="uk-button uk-button-primary uk-button-small"
              to={"/secretaria"}
            >
              Voltar
            </Link>
          </div>
        </div>
        <div className="uk-card-body">
          <div className="uk-flex uk-flex-middle">
            <div className="uk-margin-right">
              <span className="uk-badge uk-badge-success uk-text-nowrap uk-padding-small uk-margin-xsmall-bottom uk-margin-xsmall-top">
                {totalLetters} {totalLetters === 1 ? "Carta Emitida" : "Cartas Emitidas"}
              </span>
            </div>            
          </div>
          <div className="uk-grid-small uk-margin-top" uk-grid="true">
            <div className="uk-search uk-search-default uk-width-1-2@m">
              <span
                className="uk-search-icon-flip"
                uk-search-icon="true"
              ></span>
              <input
                className="uk-search-input uk-input uk-form-small"
                type="search"
                placeholder="Pesquise por nome..."
                style={{ textTransform: "uppercase" }}
                onChange={(ev) => {
                  handleSearch(ev.target.value);
                }}
              />
            </div>
            <div className="uk-width-1-2@m">
              <div className="uk-flex uk-flex-middle">                
                <label className="uk-form-label uk-margin-small-right" htmlFor="type">Tipo</label>
                <div uk-form-custom="target: > * > span:first-child" 
                  className="uk-width-1"
                  style={{backgroundColor: '#FFF'}}>
                  <select className="uk-form-small"
                    id="type" 
                    placeholder="Tipo"
                    name="type"
                    value={type}
                    onChange={(e) => {handleType(e.target.value);}}
                  >                                    
                    <option value="">Todas</option>
                    <option value={TypeHistory.RECOMMENDATION_LETTER}>{TypeHistory.RECOMMENDATION_LETTER}</option>
                    <option value={TypeHistory.CHANGE_LETTER}>{TypeHistory.CHANGE_LETTER}</option>
                  </select>
                  <button 
                    className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                    style={{textTransform: 'none'}} 
                    type="button" 
                    tabIndex={-1}
                  >
                    <span></span>
                    <span uk-icon="icon: chevron-down"></span>
                  </button>
                </div>
              </div>
            </div>            
          </div>
          <TablePagination
            columns={columns}
            fetchData={fetchLetters}
            refresh={refresh}
          />
        </div>
      </div>
      <DialogConfirm
        open={openConfirmation}
        text={[
          `A Carta de Mudança já foi assinada e podemos processar a baixa do(a) ${name}?`,
          `Se sim, informe sua senha e clique em [Confirmar].`
        ]}
        title={`Carta de Mudança Assinada?`}
        handleConfirmation={handleProcessLetter}
        afterClose={dialogClose}
        confirmTextLabel="Informe sua senha para confirmar"
      ></DialogConfirm>
    </div>
  );
}
